// ============================================
// CALENDAR CONTEXT MENU
// ============================================
//
// t18: small popover-style menu that the Calendar view shows when the
// user right-clicks on a day cell or a task pill. Items are passed in
// by the parent (CalendarMonth/Week/Agenda) so the menu stays a
// pure-presentational component and the views own the click handlers.
//
// The menu positions itself at the cursor by listening to the
// `contextmenu` event via `onContextMenu` on its trigger container;
// the actual coordinates come from the React MouseEvent (already
// viewport-relative). We close on outside click + Escape + after a
// click on any item.

import { useEffect, useRef } from 'react';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CalendarContextMenuItem {
    id: string;
    label: string;
    /** Optional icon. We default the two common actions (create + delete) so
     *  views don't need to import lucide-react. */
    icon?: 'create' | 'open' | 'delete';
    danger?: boolean;
    disabled?: boolean;
    onClick: () => void;
}

export interface CalendarContextMenuProps {
    open: boolean;
    x: number;
    y: number;
    items: CalendarContextMenuItem[];
    onClose: () => void;
}

function ItemIcon({ kind }: { kind?: 'create' | 'open' | 'delete' }) {
    if (kind === 'create') return <Plus className="h-3.5 w-3.5" aria-hidden="true" />;
    if (kind === 'delete') return <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />;
    if (kind === 'open') return <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />;
    return null;
}

export function CalendarContextMenu({
    open,
    x,
    y,
    items,
    onClose,
}: CalendarContextMenuProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Close on outside click + Escape. We register on `document` so the
    // menu still closes when the user clicks anywhere outside it.
    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (ev: MouseEvent) => {
            if (!containerRef.current) return;
            if (containerRef.current.contains(ev.target as Node)) return;
            onClose();
        };
        const onKey = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    // Close on scroll / resize so the menu doesn't drift away from its
    // anchor position.
    useEffect(() => {
        if (!open) return;
        const close = () => onClose();
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open, onClose]);

    if (!open) return null;

    // Clamp to viewport so the menu never opens off-screen. We assume a
    // menu width of ~220px and item height of ~30px.
    const MENU_W = 220;
    const MENU_H = items.length * 30 + 8;
    const maxX = typeof window !== 'undefined' ? window.innerWidth - MENU_W - 4 : x;
    const maxY = typeof window !== 'undefined' ? window.innerHeight - MENU_H - 4 : y;
    const left = Math.max(4, Math.min(x, maxX));
    const top = Math.max(4, Math.min(y, maxY));

    return (
        <div
            ref={containerRef}
            role="menu"
            aria-label="Calendar context menu"
            data-testid="calendar-context-menu"
            className="fixed z-50 min-w-[200px] rounded-md border border-border bg-surface py-1 shadow-lg"
            style={{ left, top }}
            // Prevent the browser's own context menu from firing again
            // (the parent already called preventDefault, but belt-and-
            // braces for nested triggers).
            onContextMenu={(e) => e.preventDefault()}
        >
            {items.map((item) => (
                <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    onClick={() => {
                        if (item.disabled) return;
                        item.onClick();
                        onClose();
                    }}
                    className={cn(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
                        'hover:bg-white/5 focus:bg-white/5 focus:outline-none',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        item.danger ? 'text-red-400 hover:text-red-300' : 'text-foreground'
                    )}
                >
                    <ItemIcon kind={item.icon} />
                    <span className="flex-1 truncate">{item.label}</span>
                </button>
            ))}
        </div>
    );
}